import React, { useEffect, useState } from 'react';
import { Activite, Entente } from '../../../../../../common/src/entities';
import EntentePDF from './EntentePDF';
import ActivitePDF from '../../../Vitrine/Activites/PDF/ActivitePDF';
import { pdf } from '@react-pdf/renderer';
import { PDFDocument, rgb } from 'pdf-lib';
import api from '../../../../Utils/api';
import { BiFile } from 'react-icons/bi';
import { beautifyType } from './CommonHeaderPDF';
import { useRecoilState } from 'recoil';
import { etatMasquerCovid19 } from '../../../../State';

export async function mergePDFs(pdfsToMerge: Blob[]): Promise<Blob> {
    const mergedPdf = await PDFDocument.create();
    for (const pdfBytes of pdfsToMerge) {
        if (pdfBytes.type === 'application/pdf') {
            const pdf = await PDFDocument.load(await pdfBytes.arrayBuffer());
            const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
            copiedPages.forEach((page) => {
                mergedPdf.addPage(page);
            });
        }
    }
    const bytes = await mergedPdf.save();
    return new Blob([bytes], { type: 'application/pdf' });
}

export function useLoadPdf(
    entente: Entente,
    type: string,
    masquerCovid: boolean,
    onPdfLoaded?: (name: string, data: Blob) => void,
) {
    const [loading, setLoading] = useState(true);
    const [blob, setBlob] = useState<Blob | undefined>();
    const [data, setData] = useState<Blob | undefined>();
    //reload le pdf quand l'entente, une activite de l'entente, ou le type de document change
    useEffect(() => {
        let isMounted = true;
        setBlob(undefined);

        const fetchAndMergePDFs = async () => {
            if (!entente) return;

            try {
                const ententeInstance = pdf(<EntentePDF entente={entente} type={type} />);
                const ententeBlob = await ententeInstance.toBlob();

                const activiteBlobs =
                    type === 'soumission' ? await fetchActivitesEntentePdfBlob(entente, masquerCovid) : [];

                const mergedBlob = await mergePDFs([ententeBlob, ...activiteBlobs]);

                if (isMounted) {
                    setBlob(mergedBlob);
                    setData(mergedBlob);
                    setLoading(false);

                    if (onPdfLoaded) {
                        onPdfLoaded(`Entente - ${entente.name} - ${beautifyType(type)}.pdf`, mergedBlob);
                    }
                }
            } catch (error) {
                console.error('Error generating merged PDF:', error);
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchAndMergePDFs();

        return () => {
            isMounted = false;
        };
    }, [entente, type]);

    return {
        loading,
        data: blob,
        url: data ? window.URL.createObjectURL(data) : undefined,
        name: `Entente - ${entente.name} - ${beautifyType(type)}.pdf`,
    };
}

export async function fetchActivitesEntentePdfBlob(entente: Entente, masquerCovid: boolean): Promise<Blob[]> {
    return Promise.all(
        entente.activiteEntentes.map(async (activiteEntente) => {
            const activite = await fetchActiviteVitrineEntente(activiteEntente.activite.id);

            const activiteInstance = pdf(
                <ActivitePDF activite={activite ?? new Activite()} masquerCovid={masquerCovid} />,
            );
            const activiteBlob = await activiteInstance.toBlob();

            const pieceJointeBlob = await fetchPieceJointe(activite?.fk_id_fichier_annexe);

            if (pieceJointeBlob) {
                return mergePDFs([activiteBlob, pieceJointeBlob]);
            }
            return activiteBlob;
        }),
    );
}

export async function fetchPieceJointe(id_pj: number | undefined): Promise<Blob | undefined> {
    if (!id_pj) return undefined;

    try {
        const response = await api.get<Blob>(`/fichiers/afficher/${id_pj}`, {
            responseType: 'blob',
            timeout: 30000,
        });
        return response.data;
    } catch (error) {
        console.error('Error fetching piece_jointe:', error);
        return undefined;
    }
}

export async function fetchActiviteVitrineEntente(id: number | undefined): Promise<Activite | undefined> {
    if (!id) return undefined;

    try {
        const response = await api.get<Activite>(`/activites/vitrine/${id}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching activity:', error);
        return undefined;
    }
}

export default function LoadEntentePDF({
    entente,
    type,
    className,
    titre,
    chargement,
}: {
    entente: Entente;
    type: 'soumission' | 'reservation' | 'bonDeLivraison' | 'facture';
    className?: string;
    titre?: React.ReactNode;
    chargement?: React.ReactNode;
}) {
    const [masquerCovid19, setMasquerCovid19] = useRecoilState(etatMasquerCovid19);
    const { loading, url, name } = useLoadPdf(entente, type, masquerCovid19.estMasquer);

    return (
        <a className={'btn ' + (loading ? 'disabled' : '') + ` ${className}`} href={url} download={name}>
            <span className="d-flex align-items-center mx-auto">
                {loading ? (
                    <>{chargement || 'Chargement...'}</>
                ) : (
                    <>
                        {titre || (
                            <>
                                <BiFile /> Télécharger le guide
                            </>
                        )}
                    </>
                )}
            </span>
        </a>
    );
}
